<script setup lang="ts">
  import { computed } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import {
    FormInput,
    DatePicker,
    IconRadio,
    IconImage,
    ColoredButton,
    ThemeProvider,
  } from '@/shared/ui';
  import { PositionUserSelector as PositionUserSelection } from '@/shared/ui';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import AddButton from '@/shared/assets/icons/AddButton.svg';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';
  import { useCreateNotification, type PeriodicityMode } from '../model/useCreateNotification';
  import { ChevronUp, ChevronDown } from 'lucide-vue-next';

  defineProps<{
    isOpen: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:isOpen', value: boolean): void;
    (e: 'notification-created'): void;
  }>();

  const {
    positions,
    users,
    selectedPositions,
    selectedUsers,
    title,
    description,
    periodicityMode,
    customDays,
    startDate,
    endDate,
    customDates,
    customDateKeys,
    isSaving,
    isFormValid,
    today,
    minEndDate,
    startDatePickerOpen,
    endDatePickerOpen,
    customDatePickerOpen,
    handleClose,
    handleSave,
    handleSaveDraft,
    setCustomDate,
    openCustomDatePicker,
    setCustomDatePickerOpen,
    addCustomDate,
    removeCustomDate,
  } = useCreateNotification(emit as unknown as (event: string, ...args: unknown[]) => void);

  const periodicityOptions: { label: string; value: PeriodicityMode }[] = [
    { label: 'Diária', value: 'DAILY' },
    { label: 'Semanal', value: 'WEEKLY' },
    { label: 'Quinzenal', value: 'BIWEEKLY' },
    { label: 'Mensal', value: 'MONTHLY' },
    { label: 'A cada', value: 'CUSTOM_DAYS' },
    { label: 'Personalizada', value: 'CUSTOM_DATES' },
  ];

  const positionsSelectedCount = computed(() => selectedPositions.value.length);
  const usersSelectedCount = computed(() => selectedUsers.value.length);

  const incrementCustomDays = () => {
    customDays.value++;
  };

  const decrementCustomDays = () => {
    if (customDays.value > 1) {
      customDays.value--;
    }
  };
</script>

<template>
  <VueFinalModal
    :model-value="isOpen"
    class="flex justify-center items-center"
    content-class="bg-white-01 rounded-2xl shadow-lg w-[1264px] max-h-[90vh] overflow-auto"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @update:model-value="!$event && handleClose()"
  >
    <ThemeProvider>
      <div class="flex flex-col h-full">
        <!-- Header -->
        <div class="pl-6 pr-10 py-3 border-b border-gray-02">
          <div class="flex justify-between items-center">
            <h2 class="font-semibold text-sm text-gray-07">
              Criar Notificação
            </h2>
            <button
              class="text-gray-400 hover:text-gray-600 transition-colors"
              @click="handleClose"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto">
          <form @submit.prevent>
            <!-- Título da Notificação -->
            <div class="px-6 py-8 border-b border-gray-02">
              <FormInput
                id="notification-title"
                v-model="title"
                label="Título da Notificação:"
                type="text"
                placeholder="Escreva o título da sua Notificação"
              />
            </div>

            <!-- Periodicidade -->
            <div class="px-6 py-8 border-b border-gray-02">
              <h4 class="font-semibold text-gray-07 text-sm pb-2">
                Periodicidade:
              </h4>
              <div class="flex flex-wrap gap-8 items-center">
                <template
                  v-for="option in periodicityOptions"
                  :key="option.value"
                >
                  <!-- "A cada" agrupa o radio + input de dias inline -->
                  <div
                    v-if="option.value === 'CUSTOM_DAYS'"
                    class="flex gap-1 items-center"
                  >
                    <IconRadio
                      v-model="periodicityMode"
                      :value="option.value"
                      :label="option.label"
                      name="periodicity"
                    />
                    <div
                      class="flex items-center gap-2 bg-white-01 border border-gray-02 rounded-lg h-[42px] px-4 py-2 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] ml-1"
                    >
                      <span class="text-sm text-gray-07 select-none">{{ customDays }} dias</span>
                      <div class="flex flex-col shrink-0 -space-y-2">
                        <button
                          type="button"
                          class="leading-none hover:opacity-60 transition-opacity"
                          @click="incrementCustomDays"
                        >
                          <ChevronUp class="h-5 w-5 text-gray-07" />
                        </button>
                        <button
                          type="button"
                          class="leading-none hover:opacity-60 transition-opacity"
                          @click="decrementCustomDays"
                        >
                          <ChevronDown class="h-5 w-5 text-gray-07" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <IconRadio
                    v-else
                    v-model="periodicityMode"
                    :value="option.value"
                    :label="option.label"
                    name="periodicity"
                  />
                </template>
              </div>
            </div>

            <!-- Datas para periodicidade padrão -->
            <div
              v-if="periodicityMode !== 'CUSTOM_DATES'"
              class="px-6 py-8 border-b border-gray-02 flex gap-8 items-start"
            >
              <div class="flex flex-col gap-2">
                <h4 class="font-semibold text-gray-07 text-sm">
                  Selecione a data de início:
                </h4>
                <button
                  type="button"
                  class="flex items-center gap-2 bg-white-01 h-[42px] px-4 py-2 border border-gray-02 rounded-lg text-gray-03 text-sm font-normal shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] focus:outline-none focus:border-[var(--theme-secondary-ink)] w-fit"
                  @click="startDatePickerOpen = true"
                >
                  <IconImage
                    :src="CalendarIcon"
                    alt="Calendário"
                    class="h-6 w-6 shrink-0"
                  />
                  <span>{{
                    startDate !== null
                      ? startDate.toLocaleDateString('pt-BR')
                      : 'Selecione uma data'
                  }}</span>
                </button>
                <DatePicker
                  :selected-date="startDate"
                  :is-open="startDatePickerOpen"
                  :min-date="today"
                  @update:selected-date="startDate = $event"
                  @update:is-open="startDatePickerOpen = $event"
                />
              </div>

              <div class="flex flex-col gap-2">
                <h4 class="font-semibold text-gray-07 text-sm">
                  Selecione a data final:
                </h4>
                <button
                  type="button"
                  class="flex items-center gap-2 bg-white-01 h-[42px] px-4 py-2 border border-gray-02 rounded-lg text-gray-03 text-sm font-normal shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] focus:outline-none focus:border-[var(--theme-secondary-ink)] w-fit"
                  @click="endDatePickerOpen = true"
                >
                  <IconImage
                    :src="CalendarIcon"
                    alt="Calendário"
                    class="h-6 w-6 shrink-0"
                  />
                  <span>{{
                    endDate !== null
                      ? endDate.toLocaleDateString('pt-BR')
                      : 'Selecione uma data'
                  }}</span>
                </button>
                <DatePicker
                  :selected-date="endDate"
                  :is-open="endDatePickerOpen"
                  :min-date="minEndDate"
                  @update:selected-date="endDate = $event"
                  @update:is-open="endDatePickerOpen = $event"
                />
              </div>
            </div>

            <!-- Datas personalizadas -->
            <div
              v-else
              class="px-6 py-8 border-b border-gray-02"
            >
              <div class="flex flex-col gap-4">
                <div
                  v-for="(date, index) in customDates"
                  :key="customDateKeys[index]"
                  class="flex items-center justify-between w-full"
                >
                  <div class="flex flex-col gap-2">
                    <h4
                      v-if="index === 0"
                      class="font-semibold text-gray-07 text-sm"
                    >
                      Selecione a data:
                    </h4>
                    <button
                      type="button"
                      class="flex items-center gap-2 bg-white-01 h-[42px] px-4 py-2 border border-gray-02 rounded-lg text-gray-03 text-sm font-normal shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] focus:outline-none focus:border-[var(--theme-secondary-ink)] w-fit"
                      @click="openCustomDatePicker(index)"
                    >
                      <IconImage
                        :src="CalendarIcon"
                        alt="Calendário"
                        class="h-6 w-6 shrink-0"
                      />
                      <span>{{
                        date !== null
                          ? date.toLocaleDateString('pt-BR')
                          : 'Selecione a data'
                      }}</span>
                    </button>
                    <DatePicker
                      :selected-date="date"
                      :is-open="customDatePickerOpen[index] ?? false"
                      :min-date="today"
                      @update:selected-date="setCustomDate(index, $event)"
                      @update:is-open="setCustomDatePickerOpen(index, $event)"
                    />
                  </div>
                  <div class="flex items-center text-[var(--theme-secondary-ink)]">
                    <button
                      v-if="index === 0"
                      type="button"
                      @click="addCustomDate"
                    >
                      <IconImage
                        data-testid="plus-icon"
                        :src="AddButton"
                        alt="Adicionar"
                        class="cursor-pointer"
                      />
                    </button>
                    <button
                      v-else
                      type="button"
                      @click="removeCustomDate(index)"
                    >
                      <IconImage
                        data-testid="minus-icon"
                        :src="DeleteButton"
                        alt="Remover"
                        class="cursor-pointer"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Seleção de Cargos e Colaboradores -->
            <div class="px-6 pt-8 pb-6 border-b border-gray-02">
              <h4 class="font-semibold text-gray-07 text-sm pb-3">
                Selecione os colaboradores:
              </h4>
              <PositionUserSelection
                :positions="positions"
                :users="users"
                :selected-positions="selectedPositions"
                :selected-users="selectedUsers"
                :positions-selected-count="positionsSelectedCount"
                :users-selected-count="usersSelectedCount"
                @update:selected-positions="selectedPositions = $event"
                @update:selected-users="selectedUsers = $event"
              />
            </div>

            <!-- Texto da Notificação -->
            <div class="px-6 py-8 border-b border-gray-02">
              <h4 class="font-semibold text-gray-07 text-sm pb-2">
                Texto da Notificação:
              </h4>
              <textarea
                id="notification-message"
                v-model="description"
                placeholder="Escreva aqui o texto que será enviado aos colaboradores"
                class="w-full h-[134px] border border-gray-02 rounded-lg px-4 py-3 text-sm resize-none shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] focus:outline-none focus:border-[var(--theme-secondary-ink)]"
              />
            </div>
          </form>
        </div>

        <!-- Footer -->
        <div class="px-6 py-8 flex justify-end gap-6">
          <ColoredButton
            text="Cancelar"
            :outlined="true"
            @click="handleClose"
          />
          <ColoredButton
            text="Salvar como Rascunho"
            :disabled="!title.trim() || isSaving"
            @click="handleSaveDraft"
          />
          <ColoredButton
            text="Salvar"
            :icon="CheckMarkIcon"
            :disabled="!isFormValid || isSaving"
            @click="handleSave"
          />
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>

<style scoped>
  .vfm-fade-enter-active,
  .vfm-fade-leave-active {
    transition: opacity 0.2s;
  }

  .vfm-fade-enter-from,
  .vfm-fade-leave-to {
    opacity: 0;
  }
</style>
